/* ---- Backoffice meeting transcripts: Zoom + Avoma (sc-863) ----
 * Mirrors apps/api .../meeting-transcripts/admin/types/meeting-transcripts-admin.types.ts,
 * .../meeting-transcripts/types/meeting-transcript-sync.types.ts and the zoom
 * sync module's types/zoom-admin.types.ts. Every route is gated
 * by `backoffice.integrations.meeting_transcripts`. */

/** Mirrors the Prisma enum MeetingTranscriptSource. */
export const MEETING_TRANSCRIPT_SOURCES = ['ZOOM', 'AVOMA'] as const
export type MeetingTranscriptSource =
  (typeof MEETING_TRANSCRIPT_SOURCES)[number]

/** Mirrors the Prisma enum MeetingRecordingLinkStatus, in lifecycle order. */
export const MEETING_LINK_STATUSES = [
  'PENDING',
  'MATCHED',
  'UNMATCHED',
] as const
export type MeetingLinkStatus = (typeof MEETING_LINK_STATUSES)[number]

/** Mirrors the Prisma enum MeetingTranscriptStatus. */
export const MEETING_TRANSCRIPT_STATUSES = [
  'PENDING',
  'READY',
  'FAILED',
] as const
export type MeetingTranscriptStatus =
  (typeof MEETING_TRANSCRIPT_STATUSES)[number]

/** Mirrors the Prisma enum MeetingTranscriptSyncStatus, in lifecycle order. */
export const MEETING_SYNC_BATCH_STATUSES = [
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
] as const
export type MeetingSyncBatchStatus =
  (typeof MEETING_SYNC_BATCH_STATUSES)[number]

/** Mirrors the Prisma enum MeetingTranscriptSyncMode. */
export const MEETING_SYNC_MODES = ['BACKFILL', 'RECONCILE'] as const
export type MeetingSyncMode = (typeof MEETING_SYNC_MODES)[number]

/** Mirrors the Prisma enum ZoomWebhookEventStatus, in lifecycle order. */
export const ZOOM_WEBHOOK_EVENT_STATUSES = [
  'PENDING',
  'PROCESSING',
  'PROCESSED',
  'FAILED',
  'IGNORED',
] as const
export type ZoomWebhookEventStatus =
  (typeof ZOOM_WEBHOOK_EVENT_STATUSES)[number]

/** Filter on a meeting's transcripts; NONE = the source gave it none we stored. */
export const MEETING_TRANSCRIPT_FILTERS = [
  'READY',
  'FAILED',
  'PENDING',
  'NONE',
] as const
export type MeetingTranscriptFilter =
  (typeof MEETING_TRANSCRIPT_FILTERS)[number]

/**
 * Mirrors the Prisma enum MeetingRecordingHubspotLink: who attached a
 * recording's HubSpot meeting. AUTO was attached by a key that names the exact
 * booking (the same calendar event or the same one-time Zoom meeting);
 * CONFIRMED is a person's choice; DISMISSED is a person saying there was none
 * (the matcher never overrides either, though a person can change them); NONE
 * has nothing attached, and waits for a person once its client is known.
 */
export const MEETING_HUBSPOT_LINK_STATES = [
  'NONE',
  'AUTO',
  'CONFIRMED',
  'DISMISSED',
] as const
export type MeetingHubspotLinkState =
  (typeof MEETING_HUBSPOT_LINK_STATES)[number]

/**
 * What ties a HubSpot meeting to a recorded call. CALENDAR (the same calendar
 * event, Avoma to HubSpot through Chili Piper), AVOMA (the Avoma link to the
 * call in the HubSpot meeting's notes) and ZOOM (the same one-time Zoom meeting
 * number) are keys that name the exact booking, and attach it by themselves;
 * CLIENT is one of the call's client's meetings, offered to a person only. A
 * link's `matchedBy` is CALENDAR, AVOMA or ZOOM.
 */
export const MEETING_LINK_EVIDENCE_KINDS = [
  'CALENDAR',
  'AVOMA',
  'ZOOM',
  'CLIENT',
] as const
export type MeetingLinkEvidenceKind =
  (typeof MEETING_LINK_EVIDENCE_KINDS)[number]

/** The kinds of evidence that attach a HubSpot meeting without a person. */
export type MeetingLinkKeyKind = Exclude<MeetingLinkEvidenceKind, 'CLIENT'>

/** What a running backfill is doing: Avoma looks up calendar ids first, then lists. */
export const MEETING_SYNC_PHASES = ['CALENDAR_IDS', 'LISTING'] as const
export type MeetingSyncPhase = (typeof MEETING_SYNC_PHASES)[number]

/** A backfill (or sweep) run of one source, as the backoffice shows it. */
export interface MeetingSyncBatchView {
  id: string
  source: MeetingTranscriptSource
  mode: MeetingSyncMode
  status: MeetingSyncBatchStatus
  /** YYYY-MM-DD style day (UTC), as the API sends it. */
  rangeFrom: string
  rangeTo: string
  /** Listed meetings that have a transcript. */
  meetingsWithTranscript: number
  /** Ingests queued (outcomes land on the recording and transcript rows). */
  queued: number
  /** Every transcript already READY. */
  alreadyComplete: number
  failed: number
  errorDetails: unknown
  startedAtIso: string
  completedAtIso: string | null
  /**
   * What a running batch is doing: an Avoma backfill first looks up Chili
   * Piper calendar ids (CALENDAR_IDS), then lists (LISTING). Null once done.
   */
  phase: MeetingSyncPhase | null
  /** HubSpot meetings done / to do, while in CALENDAR_IDS. */
  calendarIds: { done: number; total: number } | null
}

/** What is stored from one source, and its backfill. */
export interface MeetingSourceSyncState {
  meetings: Record<MeetingLinkStatus, number>
  /** Recordings by HubSpot meeting link. */
  hubspotLinks: Record<MeetingHubspotLinkState, number>
  /**
   * Recordings whose client is known but that have no HubSpot meeting (NONE):
   * what waits for a person to attach one.
   */
  needsMeeting: number
  transcripts: Record<MeetingTranscriptStatus, number>
  lastBackfill: MeetingSyncBatchView | null
  /** The backfill still PENDING or RUNNING, if any: what the page polls. */
  runningBackfill: MeetingSyncBatchView | null
}

/** The Zoom block of the status. */
export interface ZoomSourceStatus extends MeetingSourceSyncState {
  /**
   * All three S2S credentials are set. The only "is Zoom on" signal: the
   * webhook, processing and the hourly sweep run whenever it is true.
   */
  configured: boolean
  /** Webhook deliveries can be verified (and so accepted). */
  webhookSecretConfigured: boolean
  lastWebhookAtIso: string | null
  /** ZOOM_RECONCILE_CRON: when the hourly sweep runs (only while configured). */
  sweepCron: string
  /** ZOOM_RELINK_WINDOW_DAYS: how long an unmatched meeting is retried. */
  relinkWindowDays: number
}

/** The Avoma block of the status: no webhook, so its backfill and, where it runs, its sweep. */
export interface AvomaSourceStatus extends MeetingSourceSyncState {
  /** AVOMA_API_KEY is set. */
  configured: boolean
  /** AVOMA_RATE_LIMIT_PER_MINUTE: requests a minute the worker allows itself. */
  rateLimitPerMinute: number
  /**
   * The recurring sweep of recent meetings runs (the LaunchDarkly flag
   * apiAvomaSweep is on: production only). Off, Avoma is read only when a
   * backfill runs.
   */
  sweepEnabled: boolean
  /** AVOMA_SWEEP_CRON: when the sweep runs, whether it is on or not. */
  sweepCron: string
}

/** `GET backoffice/integrations/meeting-transcripts`. */
export interface MeetingTranscriptsStatus {
  zoom: ZoomSourceStatus
  avoma: AvomaSourceStatus
  /**
   * Both sources together. `meetings`, `hubspotLinks` and `needsMeeting` count
   * CALLS (a call both sources recorded counts once); `transcripts` counts
   * transcript files. A source's own block still counts that source's
   * recordings.
   */
  totals: Pick<
    MeetingSourceSyncState,
    'meetings' | 'hubspotLinks' | 'needsMeeting' | 'transcripts'
  > & {
    /** Every call, both sources together. */
    calls: number
  }
}

/**
 * What a call was for, from its attached HubSpot meeting's type as classified on
 * the meeting-types page (/backoffice/journey-scheduling/meeting-types).
 * UNCLASSIFIED: a type missing there or without a category. NO_MEETING: no
 * HubSpot meeting attached.
 */
export const MEETING_CALL_CATEGORIES = [
  'SALES',
  'SERVICE',
  'UNCLASSIFIED',
  'NO_MEETING',
] as const
export type MeetingCallCategory = (typeof MEETING_CALL_CATEGORIES)[number]

/** A HubSpot meeting type and how many calls it covers. */
export interface MeetingTypeCount {
  /** The spelling most calls carry; `null` for meetings without a type. */
  meetingType: string | null
  count: number
}

/** Calls of one category, split by whether the client was found. */
export interface MeetingCategoryCounts {
  calls: number
  clientFound: number
  noClient: number
}

/**
 * Where the calls stand, and why. A source's figures count its recordings; the
 * `totals` figures count calls (a call both sources recorded counts once). Every
 * call keeps its transcript, client or not.
 */
export interface MeetingCallsInsight {
  /** Every recorded call. */
  total: number
  /** Not linked yet (PENDING): the matcher has not run on them. */
  notCheckedYet: number
  /** The client is known (MATCHED). */
  withClient: {
    total: number
    /** A key attached the HubSpot meeting (AUTO). */
    attachedAutomatically: number
    /** A person attached it (CONFIRMED). */
    attachedByHand: number
    /** A person said there is none (DISMISSED). */
    notABookedMeeting: number
    /** Client known, no HubSpot meeting: what waits for a person. */
    needsMeeting: {
      total: number
      /** Avoma has no calendar event for the call: it was not booked from a calendar. */
      noCalendarEvent: number
      /** It has one, but no single HubSpot meeting carries it. */
      calendarEventNotInHubspot: number
      /**
       * Anything else: the reasons above are Avoma's, and a Zoom call only
       * finds its client through its meeting, so this is normally zero.
       */
      other: number
      /**
       * The client has a HubSpot meeting within 12 hours of the call. A count
       * to tell what can be attached by hand; nothing is attached by time.
       */
      clientHasMeetingSameDay: number
    }
  }
  /** No client found (UNMATCHED); a sales call with a prospect is one. */
  noClient: number
  /**
   * By category: Sales / Service from the meeting-types page, UNCLASSIFIED and
   * NO_MEETING. No client is expected on a sales call (a prospect) and worth a
   * look on a service one. The calls of the four add up to `total`.
   */
  byCategory: Record<MeetingCallCategory, MeetingCategoryCounts>
  /** The HubSpot meeting types not classified yet, most calls first (top 5). */
  unclassifiedTypes: Array<MeetingTypeCount>
}

/** `GET backoffice/integrations/meeting-transcripts/insights`. */
export interface MeetingTranscriptsInsights {
  zoom: MeetingCallsInsight
  avoma: MeetingCallsInsight
  /** Both sources together, each call once. */
  totals: MeetingCallsInsight
}

/**
 * What a link into the Meetings tab sets. Each field is a filter the tab
 * already has (`source`, `category`, `linkStatus`, `hubspotLink`,
 * `meetingType`), so no new URL param is needed; a field left out is not
 * filtered.
 */
export interface MeetingsLinkFilter {
  source?: MeetingTranscriptSource
  /** One category of call (Sales, Service, ...). */
  category?: MeetingCallCategory
  linkStatus?: MeetingLinkStatus
  hubspotLink?: MeetingHubspotLinkState
  /** The label of one meeting type, as `GET meeting-types` lists it. */
  meetingType?: string
}

/** One received Zoom webhook delivery. */
export interface ZoomWebhookEventRow {
  id: string
  eventType: string
  zoomMeetingUuid: string | null
  processingStatus: ZoomWebhookEventStatus
  errorMessage: string | null
  attemptCount: number
  receivedAtIso: string
  processedAtIso: string | null
}

export interface ZoomWebhookEventsPage {
  page: number
  pageSize: number
  total: number
  /** Newest first. */
  items: Array<ZoomWebhookEventRow>
}

/** Query of the deliveries list. The API defaults to page 1, 25 per page (max 100). */
export interface ZoomWebhookEventsQuery {
  status?: ZoomWebhookEventStatus
  page?: number
  pageSize?: number
}

export interface ZoomWebhookReplayResult {
  eventId: string
  /** The BullMQ job id, or null when the queue gave none back. */
  jobId: string | null
}

/** A staff user as the backoffice tables show one. */
export interface MeetingPersonRef {
  id: string
  name: string | null
  email: string
}

/** The meeting's host: the source's email always, the Rothology user when known. */
export interface MeetingHostRef {
  userId: string | null
  name: string | null
  email: string
}

export interface MeetingClientRef {
  id: string
  name: string
}

/** The HubSpot meeting the recorded call was matched to. */
export interface MeetingHubspotMeetingRef {
  /** Our mirror row id (`hubspot_meetings.id`). */
  id: string
  hubspotId: string
  title: string | null
  /** HubSpot's meeting type (hs_activity_type), trimmed; null when unset. */
  meetingType: string | null
  startTimeIso: string | null
  endTimeIso: string | null
  /** HubSpot's hs_meeting_outcome (COMPLETED, NO_SHOW, CANCELED, ...); null when not recorded. */
  outcome: string | null
  /** Contact timeline with the meeting highlighted; null without a portal id. */
  url: string | null
}

export interface MeetingTranscriptCounts {
  total: number
  ready: number
  failed: number
  pending: number
}

/** A HubSpot contact a call is tied to, whether or not it is a client. */
export interface MeetingHubspotContactRef {
  /** HubSpot's contact id. */
  id: string
  /** The contact's HubSpot record; null without a portal id. */
  url: string | null
  /** The client's own contact when the client is known, else the first. */
  isMain: boolean
}

/** Mirrors the Prisma enum MeetingRecordingTagSource: how a call came to carry a tag. */
export const MEETING_CALL_TAG_SOURCES = ['RULE', 'MANUAL'] as const
export type MeetingCallTagSource = (typeof MEETING_CALL_TAG_SOURCES)[number]

/**
 * A tag on a call (sc-863 Phase B). `color` is a UI tone name (info, warning,
 * success, neutral, violet, ...), never a CSS value. A call carrying a
 * confidential tag is visible only to people granted
 * `backoffice.integrations.meeting_transcripts_confidential`.
 */
export interface MeetingCallTagRef {
  id: string
  name: string
  color: string
  isConfidential: boolean
  /** RULE: a tag rule added it; MANUAL: a person did. */
  source: MeetingCallTagSource
}

/** The same call recorded by the other source (same HubSpot meeting). */
export interface MeetingRecordingCopy {
  id: string
  source: MeetingTranscriptSource
  startTimeIso: string
  linkStatus: MeetingLinkStatus
  transcripts: MeetingTranscriptCounts
}

/**
 * One row of `GET backoffice/integrations/meeting-transcripts/meetings`: ONE
 * CALL. When both sources recorded a call (the same HubSpot meeting) the Zoom
 * recording is the row and the Avoma one is in `copies`.
 */
export interface MeetingRecordingRow {
  id: string
  source: MeetingTranscriptSource
  /** Zoom: the meeting instance uuid. Avoma: the meeting uuid. */
  externalId: string
  /** Null for an Avoma call that was not held on Zoom. */
  zoomMeetingNumber: string | null
  topic: string | null
  startTimeIso: string
  durationMinutes: number | null
  host: MeetingHostRef
  linkStatus: MeetingLinkStatus
  linkAttempts: number
  lastLinkAttemptAtIso: string | null
  matchedAtIso: string | null
  client: MeetingClientRef | null
  /** Who attached the HubSpot meeting: the matcher (AUTO), a person, or nobody. */
  hubspotLink: MeetingHubspotLinkState
  /** The attached HubSpot meeting (AUTO or CONFIRMED). */
  hubspotMeeting: MeetingHubspotMeetingRef | null
  /** Sales or Service, from the attached meeting's type (see MeetingCallCategory). */
  category: MeetingCallCategory
  /**
   * The HubSpot contacts the call is tied to, client or not (a sales call keeps
   * its prospect), the main one first.
   */
  hubspotContacts: Array<MeetingHubspotContactRef>
  /** Its tags, by the tags' order (a tag a person removed is not one). */
  tags: Array<MeetingCallTagRef>
  transcripts: MeetingTranscriptCounts
  /** The other source's recording of the same call; empty when only one source recorded it. */
  copies: Array<MeetingRecordingCopy>
}

/**
 * Where a transcript of the call says what was searched: a snippet around it,
 * with the matched words wrapped between {@link SEARCH_MARK_OPEN} and
 * {@link SEARCH_MARK_CLOSE} (U+27E6 and U+27E7). Plain text otherwise: the
 * markers are split on, never rendered as HTML.
 */
export interface MeetingSearchHit {
  transcriptId: string
  recordingId: string
  snippet: string
}

/** One item of the meetings page: a call, and (when searched by what was said) where it says it. */
export type MeetingRecordingListItem = MeetingRecordingRow & {
  /** Set when the list was searched by what was said (`q`); null otherwise. */
  searchHit: MeetingSearchHit | null
}

export interface MeetingRecordingsPage {
  page: number
  pageSize: number
  total: number
  items: Array<MeetingRecordingListItem>
}

/** What brackets a matched word in a {@link MeetingSearchHit} snippet. */
export const SEARCH_MARK_OPEN = '\u27E6'
export const SEARCH_MARK_CLOSE = '\u27E7'

/** Query of `GET backoffice/integrations/meeting-transcripts/meetings`. */
export interface MeetingRecordingsQuery {
  /**
   * Sources to include; omitted = all. Call-level: Avoma also matches a Zoom
   * row that has an Avoma copy.
   */
  source?: Array<MeetingTranscriptSource>
  /** Categories of call to include; omitted = all. */
  category?: Array<MeetingCallCategory>
  linkStatus?: Array<MeetingLinkStatus>
  /** HubSpot meeting link states to include; omitted = all. */
  hubspotLink?: Array<MeetingHubspotLinkState>
  /** Only calls whose client is known and that have no HubSpot meeting (NONE). */
  needsMeeting?: boolean
  /** The label of one meeting type, as `GET meeting-types` lists it; the API matches every spelling. */
  meetingType?: string
  transcriptStatus?: MeetingTranscriptFilter
  /** YYYY-MM-DD, inclusive, UTC. */
  startFrom?: string
  /** YYYY-MM-DD, inclusive, UTC. */
  startTo?: string
  hostUserId?: string
  search?: string
  /** Ids of the tags to include: calls carrying any of them; omitted = all. */
  tag?: Array<string>
  /**
   * What was said: full-text search over the calls' transcripts (English
   * stemming, Google-like syntax: "quoted phrase", -word, OR). Each item then
   * carries a `searchHit`. Distinct from `search`, which matches topic, host,
   * client and Zoom meeting number.
   */
  q?: string
  page?: number
  pageSize?: number
}

/** The Meetings tab's filter controls, as the controls hold them. */
export interface MeetingsFilterState {
  /** `undefined` = every source. */
  source: MeetingTranscriptSource | undefined
  /** Empty = every category. */
  category: Array<MeetingCallCategory>
  linkStatus: Array<MeetingLinkStatus>
  /** `undefined` = any HubSpot meeting link state. Never set together with `needsMeeting`. */
  hubspotLink: MeetingHubspotLinkState | undefined
  /** Only calls with a known client and no HubSpot meeting: the queue a person works through. */
  needsMeeting: boolean
  /** `undefined` = any HubSpot meeting type. */
  meetingType: string | undefined
  /** `undefined` = any transcript state. */
  transcriptStatus: MeetingTranscriptFilter | undefined
  /** ISO instants from the date pickers; only the day part is sent. */
  from: string | null
  to: string | null
  search: string
  /** Ids of the tags: calls carrying any of them. Empty = every call. */
  tag: Array<string>
  /** What was said (transcript search). Distinct from `search`. */
  q: string
}

/** A stored transcript, without its text. */
export interface MeetingTranscriptSummary {
  id: string
  /** The recording (copy of the call) the transcript belongs to. */
  recordingId: string
  source: MeetingTranscriptSource
  /** Zoom: the TRANSCRIPT file id. Avoma: the transcription uuid. */
  externalId: string
  status: MeetingTranscriptStatus
  recordingStartIso: string | null
  recordingEndIso: string | null
  speakerNames: Array<string>
  cueCount: number
  /** Characters of `fullText`; 0 until READY. */
  textLength: number
  /** The raw file is archived in the private bucket. */
  archived: boolean
  fetchedAtIso: string | null
  errorMessage: string | null
}

/** The HubSpot meeting link of one recording, with its review. */
export interface MeetingHubspotLinkReview {
  state: MeetingHubspotLinkState
  /** What attached it, when the matcher did (AUTO); null otherwise. */
  matchedBy: MeetingLinkKeyKind | null
  /** Who attached or dismissed it; null while the matcher decided. */
  reviewedBy: MeetingPersonRef | null
  reviewedAtIso: string | null
}

/**
 * Why the attach dialog suggests a HubSpot meeting: ZOOM_COPY (the Zoom
 * recording of this call is attached to it; attaching merges the two),
 * HOST_MEETING (the call's host owns it and it starts within an hour),
 * SAME_START (within five minutes), SAME_TITLE, SAME_LENGTH (only beside
 * another reason). A suggestion is a highlight for the person choosing; nothing
 * is attached by it.
 */
export const MEETING_SUGGESTION_REASONS = [
  'ZOOM_COPY',
  'HOST_MEETING',
  'SAME_START',
  'SAME_TITLE',
  'SAME_LENGTH',
] as const
export type MeetingSuggestionReason =
  (typeof MEETING_SUGGESTION_REASONS)[number]

/** One HubSpot meeting a person may choose for a recording. */
export interface MeetingHubspotCandidate extends MeetingHubspotMeetingRef {
  /** Minutes between the call's start and the booked start (no direction). */
  deltaMinutes: number
  /** Every kind of evidence that ties it to the call. */
  matchedBy: Array<MeetingLinkEvidenceKind>
  /** Why it is likely the call, strongest first; empty when not suggested. */
  suggestedBecause: Array<MeetingSuggestionReason>
  /** Clients the HubSpot meeting is associated with. */
  clients: Array<MeetingClientRef>
  isLinked: boolean
}

/** One option of the meeting-type filter: every spelling of one type. */
export interface MeetingTypeOption {
  /** The label shown and sent back as `meetingType`. */
  value: string
  /** Recordings attached to a HubSpot meeting of this type. */
  count: number
}

/** `GET backoffice/integrations/meeting-transcripts/meeting-types`, most used first. */
export interface MeetingTypeOptions {
  items: Array<MeetingTypeOption>
}

/** `GET backoffice/integrations/meeting-transcripts/meetings/:id/hubspot-candidates`. */
export interface MeetingHubspotCandidates {
  recordingId: string
  /**
   * The client whose HubSpot meetings are listed: the one asked for, else the
   * recording's. Null when neither is known; then the list is the suggestions
   * alone (the host's own meetings around the call, the meeting its Zoom copy
   * has, any a calendar event or Zoom number names) and a person can pick a
   * client to see all theirs.
   */
  clientId: string | null
  /**
   * Keyed (exact) ones first, then the suggested ones (the Zoom copy's meeting
   * first), then the rest, nearest to the call first. A client's list is all of
   * their HubSpot meetings (up to 200).
   */
  items: Array<MeetingHubspotCandidate>
}

/** `GET backoffice/integrations/meeting-transcripts/meetings/:id`. */
export interface MeetingRecordingDetail extends MeetingRecordingRow {
  hubspotLinkReview: MeetingHubspotLinkReview
  /** The transcripts of this call: this recording's and its copies' (each says which in `recordingId`). */
  transcriptFiles: Array<MeetingTranscriptSummary>
  /** Deliveries Zoom sent about this meeting, newest first; empty for Avoma. */
  webhookEvents: Array<ZoomWebhookEventRow>
}

export interface MeetingTranscriptCueView {
  ordinal: number
  /** Milliseconds from the start of this transcript's recording. */
  startMs: number
  endMs: number
  speaker: string | null
  /** The speaker's email, when the source knows it (Avoma). */
  speakerEmail: string | null
  /** True for Q3 staff, false for the client; null when the source does not say (Zoom). */
  speakerIsStaff: boolean | null
  text: string
}

/** `GET backoffice/integrations/meeting-transcripts/transcripts/:id`: the whole transcript. */
export interface MeetingTranscriptView {
  id: string
  source: MeetingTranscriptSource
  status: MeetingTranscriptStatus
  speakerNames: Array<string>
  recordingStartIso: string | null
  recordingEndIso: string | null
  fetchedAtIso: string | null
  meeting: {
    id: string
    source: MeetingTranscriptSource
    topic: string | null
    startTimeIso: string
    host: MeetingHostRef
    client: MeetingClientRef | null
  }
  /** Speaker turns as prose, for Copy. */
  fullText: string | null
  cues: Array<MeetingTranscriptCueView>
}

/** `POST backoffice/integrations/meeting-transcripts/meetings/:id/ingest`. */
export interface MeetingIngestRequestResult {
  recordingId: string
  source: MeetingTranscriptSource
  queued: true
}

/** One row of `GET backoffice/integrations/meeting-transcripts/batches`. */
export interface MeetingSyncBatchHistoryRow extends MeetingSyncBatchView {
  initiatedBy: MeetingPersonRef | null
}

export interface MeetingSyncBatchesPage {
  page: number
  pageSize: number
  total: number
  items: Array<MeetingSyncBatchHistoryRow>
}

/** Body of `POST backoffice/integrations/meeting-transcripts/backfill`. */
export interface MeetingBackfillPayload {
  source: MeetingTranscriptSource
  /** YYYY-MM-DD, UTC. */
  from: string
  /** YYYY-MM-DD, UTC, inclusive. The API defaults to today. */
  to?: string
}

/** Body of `POST backoffice/integrations/meeting-transcripts/meetings/:id/hubspot-link`. */
export interface MeetingHubspotLinkReviewPayload {
  /** Our `hubspot_meetings.id` the recording was, or `null` for "not a booked meeting". */
  hubspotMeetingId: string | null
  /**
   * The client whose meetings the person picked from; it becomes the
   * recording's client. The API answers 400 when the meeting is not associated
   * with it, or when it is sent with a null meeting.
   */
  clientId?: string
}

/** Mirrors the Prisma enum MeetingCallTagRuleKind. */
export const MEETING_CALL_TAG_RULE_KINDS = [
  'ZOOM_MEETING_NUMBER',
  'TOPIC_CONTAINS',
] as const
export type MeetingCallTagRuleKind =
  (typeof MEETING_CALL_TAG_RULE_KINDS)[number]

/** A tag rule: it tags matching calls by itself, now and as they come in. */
export interface MeetingCallTagRuleView {
  id: string
  /** ZOOM_MEETING_NUMBER: the call's Zoom number; TOPIC_CONTAINS: in its topic. */
  kind: MeetingCallTagRuleKind
  value: string
  /** The rule only tags this host's calls; null for anyone's. */
  host: MeetingPersonRef | null
  isActive: boolean
  /** Calls the rule tagged that still carry the tag. */
  taggedCalls: number
  createdBy: MeetingPersonRef | null
  createdAtIso: string
}

/** A tag with its rules (`GET backoffice/integrations/meeting-transcripts/tags`). */
export interface MeetingCallTagView {
  id: string
  name: string
  color: string
  isConfidential: boolean
  sortOrder: number
  /** Calls carrying it, by rule or by hand. */
  taggedCalls: number
  rules: Array<MeetingCallTagRuleView>
}

export interface MeetingCallTagsList {
  /** Confidential tags are listed only to holders of the confidential permission. */
  items: Array<MeetingCallTagView>
  /** The viewer may see and manage confidential calls and tags. */
  canManageConfidential: boolean
}

/** What creating a rule did. */
export interface MeetingCallTagRuleCreated {
  rule: MeetingCallTagRuleView
  /** Calls it tagged at once. */
  taggedNow: number
}

/** Body of `POST backoffice/integrations/meeting-transcripts/tags`. */
export interface MeetingCallTagCreatePayload {
  /** Unique, at most 80 characters. */
  name: string
  /** A UI tone name; the API defaults to `neutral`. */
  color?: string
  /** Only sent by a holder of the confidential permission. */
  isConfidential?: boolean
}

/** Body of `PATCH backoffice/integrations/meeting-transcripts/tags/:tagId`. */
export interface MeetingCallTagUpdatePayload {
  name?: string
  color?: string
  isConfidential?: boolean
}

/** Body of `POST backoffice/integrations/meeting-transcripts/tags/:tagId/rules`. */
export interface MeetingCallTagRuleCreatePayload {
  kind: MeetingCallTagRuleKind
  /** At most 200 characters. */
  value: string
  /** Only this host's calls. The page has no control for it; a rule made elsewhere may carry one. */
  hostUserId?: string
}

/** Body of `PATCH backoffice/integrations/meeting-transcripts/tags/rules/:ruleId`. */
export interface MeetingCallTagRuleUpdatePayload {
  /** Off: the calls the rule tagged lose the tag (a hand-made tag stays). */
  isActive?: boolean
}

/* ---- Meeting outcomes (sc-863 Phase C) ----
 * Mirrors apps/api .../meeting-transcripts/admin/types/meeting-outcomes-admin.types.ts.
 * Whether a HubSpot meeting happened, decided from Zoom attendance and Avoma, or
 * by a person. Same gate as the rest of the page. */

/** Mirrors the Prisma enum MeetingOutcome. */
export const MEETING_OUTCOMES = ['COMPLETED', 'NO_SHOW'] as const
export type MeetingOutcome = (typeof MEETING_OUTCOMES)[number]

/** Mirrors the Prisma enum MeetingOutcomeConfidence. */
export const MEETING_OUTCOME_CONFIDENCES = [
  'SURE',
  'LIKELY',
  'UNKNOWN',
] as const
export type MeetingOutcomeConfidence =
  (typeof MEETING_OUTCOME_CONFIDENCES)[number]

/** Mirrors the Prisma enum MeetingOutcomeReason. */
export const MEETING_OUTCOME_REASONS = [
  'CLIENT_JOINED_ZOOM',
  'CLIENT_SPOKE_AVOMA',
  'ONLY_Q3_JOINED_ZOOM',
  'ONLY_Q3_SPOKE_AVOMA',
  'AVOMA_SILENT_RECORDING',
  'AVOMA_MARKED_NO_SHOW',
  'ZOOM_ROOM_REUSED_ONLY_Q3',
  'ZOOM_NOT_STARTED',
  'AVOMA_BOT_NOT_ADMITTED',
  'CLIENT_TALKED_BY_PHONE',
  'NO_SIGNAL',
] as const
export type MeetingOutcomeReason = (typeof MEETING_OUTCOME_REASONS)[number]

/** Mirrors the Prisma enum MeetingOutcomePushResult: how the last write to HubSpot went. */
export const MEETING_OUTCOME_PUSH_RESULTS = [
  'UPDATED',
  'NOT_FOUND',
  'REJECTED',
  'UNKNOWN',
] as const
export type MeetingOutcomePushResult =
  (typeof MEETING_OUTCOME_PUSH_RESULTS)[number]

/** Mirrors the Prisma enum ZoomAttendanceStatus. */
export const ZOOM_ATTENDANCE_STATUSES = [
  'FOUND',
  'NOT_STARTED',
  'FAILED',
] as const
export type ZoomAttendanceStatus = (typeof ZOOM_ATTENDANCE_STATUSES)[number]

/**
 * Which meetings the Outcomes tab lists: the ones nobody has settled
 * (`NEEDS_PERSON`, the default), the ones where Rothology and HubSpot differ
 * (`DISAGREES`), or all of them.
 */
export const MEETING_OUTCOME_VIEWS = [
  'NEEDS_PERSON',
  'DISAGREES',
  'ALL',
] as const
export type MeetingOutcomeView = (typeof MEETING_OUTCOME_VIEWS)[number]

/** What Rothology says, for filtering: an outcome, or UNKNOWN when it can't tell. */
export const MEETING_OUTCOME_FILTERS = [
  'COMPLETED',
  'NO_SHOW',
  'UNKNOWN',
] as const
export type MeetingOutcomeFilter = (typeof MEETING_OUTCOME_FILTERS)[number]

/** The categories whose meetings get an outcome. */
export const MEETING_OUTCOME_CATEGORIES = ['SALES', 'SERVICE'] as const
export type MeetingOutcomeCategory = (typeof MEETING_OUTCOME_CATEGORIES)[number]

/** Who joined the meeting's Zoom, as counts. */
export interface MeetingOutcomeZoomView {
  status: ZoomAttendanceStatus
  /** The occurrence is the booking's own; false for a reused room. */
  exact: boolean
  internalCount: number
  externalCount: number
  externalMinutes: number
  botCount: number
  fetchedAtIso: string
}

export interface MeetingOutcomeCheckView {
  /** What the evidence says; null when it can't tell. */
  detected: MeetingOutcome | null
  confidence: MeetingOutcomeConfidence
  reason: MeetingOutcomeReason
  checkedAtIso: string
  /** A person's call, final. */
  decided: MeetingOutcome | null
  decidedBy: { id: string; name: string } | null
  decidedAtIso: string | null
  /**
   * A person's decision never sent for its outcome: it was made while writing
   * to HubSpot was off. It stays in Rothology and is not sent on its own when
   * writing is turned on.
   */
  decisionHeld: boolean
  /** The last outcome sent to HubSpot, and how that went. */
  pushedOutcome: MeetingOutcome | null
  pushedAtIso: string | null
  pushResult: MeetingOutcomePushResult | null
}

/** One HubSpot meeting on the Outcomes tab. */
export interface MeetingOutcomeRow {
  hubspotMeeting: MeetingHubspotMeetingRef
  category: MeetingCallCategory
  clients: Array<MeetingClientRef>
  /** The HubSpot owner, as a Rothology user. */
  host: { id: string; name: string } | null
  /** A recorded call of the meeting, to open in the call sheet. */
  callId: string | null
  check: MeetingOutcomeCheckView | null
  zoom: MeetingOutcomeZoomView | null
  /**
   * The longest phone call (Avoma's dialer) the host had with the meeting's
   * contact during its slot, the client speaking, in minutes. Null when none.
   * From 5 minutes it makes the meeting COMPLETED (CLIENT_TALKED_BY_PHONE).
   */
  phoneCallMinutes: number | null
}

/** What Avoma said about the meeting, recorded or not. */
export interface MeetingOutcomeAvomaStateView {
  /** scheduled, in_progress, completed, not_recorded, cancelled. */
  state: string
  /** Why it was or was not recorded (silent_rec, bot_waiting_for_host_timeout, ...). */
  processingStatus: string | null
  /** The outcome a rep picked in Avoma ("Completed", "No-Show", ...). */
  outcomeLabel: string | null
  startTimeIso: string
}

/** A recorded call that is evidence for the meeting's outcome. */
export interface MeetingOutcomeRecordingView {
  /** `meeting_recordings.id`: opens the call sheet. */
  id: string
  source: MeetingTranscriptSource
  startTimeIso: string
  durationMinutes: number | null
  /** An Avoma dialer phone call rather than a video meeting. */
  isCall: boolean
  /** Attached to this HubSpot meeting; false for a phone call found by the host, the contact and the slot. */
  attached: boolean
  /** A client spoke in it. Null for Zoom, which does not say. */
  clientSpoke: boolean | null
}

/** Everything the outcome was decided from, for the details sheet. */
export interface MeetingOutcomeEvidenceView {
  avomaStates: Array<MeetingOutcomeAvomaStateView>
  recordings: Array<MeetingOutcomeRecordingView>
}

/** `GET outcomes/:hubspotMeetingId` and a decision's answer: a row with its evidence. */
export interface MeetingOutcomeDetail extends MeetingOutcomeRow {
  evidence: MeetingOutcomeEvidenceView
}

export interface MeetingOutcomesPage {
  page: number
  pageSize: number
  total: number
  /** False = shadow mode: outcomes are decided and shown, nothing is written to HubSpot. */
  writeEnabled: boolean
  /** Newest first. */
  items: Array<MeetingOutcomeRow>
}

/** One category's meetings in the summary window. */
export interface MeetingOutcomeCategorySummary {
  /** Ended meetings in the window, cancelled and moved ones left out. */
  meetings: number
  /** A person said so, or the evidence is sure. */
  completed: number
  noShow: number
  /** The evidence suggests a no-show, waiting for a person. */
  likelyNoShow: number
  /** Nothing decides it yet. */
  cantTell: number
  decidedByPerson: number
  /** HubSpot says COMPLETED where Rothology says no-show (sure or a person). */
  hubspotCompletedButNoShow: number
  /** HubSpot says COMPLETED where the evidence suggests a no-show. */
  hubspotCompletedButLikelyNoShow: number
  /** HubSpot says NO_SHOW where the client was there. */
  hubspotNoShowButCompleted: number
}

/** `GET .../outcomes/summary`: the Overview's "Outcomes vs HubSpot" panel. */
export interface MeetingOutcomesSummary {
  fromIso: string
  toIso: string
  writeEnabled: boolean
  byCategory: Record<MeetingOutcomeCategory, MeetingOutcomeCategorySummary>
}

/** Query of `GET backoffice/integrations/meeting-transcripts/outcomes`. */
export interface MeetingOutcomesQuery {
  /** The API defaults to NEEDS_PERSON. */
  view?: MeetingOutcomeView
  /** What Rothology says; omitted = any. */
  outcome?: Array<MeetingOutcomeFilter>
  /** Omitted = both. */
  category?: Array<MeetingOutcomeCategory>
  /** YYYY-MM-DD, inclusive, UTC, on the meeting's end. The API defaults to the last 30 days. */
  endFrom?: string
  endTo?: string
  page?: number
  pageSize?: number
}

/** Body of `POST .../outcomes/:hubspotMeetingId`; `null` clears a person's decision. */
export interface MeetingOutcomeDecisionPayload {
  outcome: MeetingOutcome | null
}

/** The Outcomes tab's filter controls, as the controls hold them. */
export interface MeetingOutcomesFilterState {
  view: MeetingOutcomeView
  /** Empty = whatever Rothology says. */
  outcome: Array<MeetingOutcomeFilter>
  /** Empty = Sales and Service. */
  category: Array<MeetingOutcomeCategory>
  /** ISO instants from the date pickers on the meeting's end; only the day part is sent. */
  from: string | null
  to: string | null
}

/**
 * What a link into the Outcomes tab sets: the Overview's figures land on the
 * list they count. Each field is a filter the tab already has; a field left
 * out is not set (the view then being the tab's default).
 */
export interface MeetingOutcomesLinkFilter {
  view?: MeetingOutcomeView
  outcome?: Array<MeetingOutcomeFilter>
  category?: Array<MeetingOutcomeCategory>
}
